import { LinkIcon, LinkSlashIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { NumberInputWithUnit } from '../number-input-with-unit';
import type { UnitOption } from '../number-input-with-unit/types';
import {
  CenterButton,
  Container,
  Content,
  InputLabel,
  InputsGrid,
  InputWrapper,
  LinkButton,
} from './styled';
import type { BoxInputProps, BoxInputValues } from './types';

const DEFAULT_UNITS: UnitOption[] = [
  { value: 'px', label: 'Pixels', abbreviation: 'px' },
  { value: 'em', label: 'Em', abbreviation: 'em' },
  { value: 'rem', label: 'Rem', abbreviation: 'rem' },
  { value: '%', label: 'Percent', abbreviation: '%' },
];

const DEFAULT_SIDE_LABELS = {
  top: 'Top',
  right: 'Right',
  bottom: 'Bottom',
  left: 'Left',
};

const DEFAULT_CORNER_LABELS = {
  top: 'Top Left',
  right: 'Top Right',
  bottom: 'Bottom Right',
  left: 'Bottom Left',
};

export const BoxInput: React.FC<BoxInputProps> = ({
  variant = 'outline',
  size = 'medium',
  layout = 'sides',
  error = false,
  className,
  testId,
  styleProps,
  values: controlledValues,
  onChange,
  unitOptions = DEFAULT_UNITS,
  labels,
  placeholder = '0',
  disabled = false,
  min,
  max,
  step = 1,
  precision = 0,
  allowNegative = true,
  showControls = false,
}) => {

  const [internalValues, setInternalValues] = useState<BoxInputValues>({
    top: '',
    right: '',
    bottom: '',
    left: '',
  });

  const [internalLinked, setInternalLinked] = useState<boolean>(() => {

    const initialValues = controlledValues || internalValues;
    const values = [
      initialValues.top,
      initialValues.right,
      initialValues.bottom,
      initialValues.left,
    ];
    const nonEmptyValues = values.filter((val) => val && val.trim() !== '');

    if (nonEmptyValues.length === 0) return false;

    const firstValue = nonEmptyValues[0];
    return (
      nonEmptyValues.every((val) => val === firstValue) &&
      nonEmptyValues.length === values.filter((val) => val !== undefined).length
    );
  });

  const isValuesControlled = controlledValues !== undefined;

  const currentValues = useMemo(
    () => (isValuesControlled ? controlledValues || {} : internalValues),
    [isValuesControlled, controlledValues, internalValues]
  );

  const currentLinked = internalLinked;

  useEffect(() => {
    if (!isValuesControlled && controlledValues) {
      setInternalValues(controlledValues);
    }
  }, [controlledValues, isValuesControlled]);

  const handleValueChange = useCallback(
    (side: 'top' | 'right' | 'bottom' | 'left', combinedValue: string) => {
      const newValues = { ...currentValues };

      if (currentLinked) {

        newValues.top = combinedValue;
        newValues.right = combinedValue;
        newValues.bottom = combinedValue;
        newValues.left = combinedValue;
      } else {

        newValues[side] = combinedValue;
      }

      if (!isValuesControlled) {
        setInternalValues(newValues);
      }

      onChange?.(newValues);
    },
    [currentValues, currentLinked, isValuesControlled, onChange]
  );

  const handleLinkToggle = useCallback(() => {
    const newLinked = !currentLinked;
    setInternalLinked(newLinked);

  }, [currentLinked]);

  const LinkIconComponent = currentLinked ? LinkIcon : LinkSlashIcon;

  const defaultLabels = layout === 'corners' ? DEFAULT_CORNER_LABELS : DEFAULT_SIDE_LABELS;
  const effectiveLabels = labels || defaultLabels;

  return (
    <Container
      className={className}
      $variant={variant}
      $size={size}
      $layout={layout}
      $error={error}
      $styleProps={styleProps}
      data-testid={testId}
    >
      <Content $size={size} $layout={layout} $styleProps={styleProps}>
        <InputsGrid $size={size} $layout={layout} $styleProps={styleProps}>
          {}
          <InputWrapper $position='top' $layout={layout}>
            <InputLabel $size={size} $styleProps={styleProps}>
              {effectiveLabels.top}
            </InputLabel>
            <NumberInputWithUnit
              variant={variant}
              size={size}
              error={error}
              styleProps={styleProps}
              value={currentValues.top}
              unitOptions={unitOptions}
              onChange={(combinedValue) => handleValueChange('top', combinedValue)}
              placeholder={placeholder}
              disabled={disabled}
              min={min}
              max={max}
              step={step}
              precision={precision}
              allowNegative={allowNegative}
              showControls={showControls}
            />
          </InputWrapper>

          {}
          <InputWrapper $position='right' $layout={layout}>
            <InputLabel $size={size} $styleProps={styleProps}>
              {effectiveLabels.right}
            </InputLabel>
            <NumberInputWithUnit
              variant={variant}
              size={size}
              error={error}
              styleProps={styleProps}
              value={currentValues.right}
              unitOptions={unitOptions}
              onChange={(combinedValue) => handleValueChange('right', combinedValue)}
              placeholder={placeholder}
              disabled={disabled}
              min={min}
              max={max}
              step={step}
              precision={precision}
              allowNegative={allowNegative}
              showControls={showControls}
            />
          </InputWrapper>

          {}
          <InputWrapper $position='bottom' $layout={layout}>
            <InputLabel $size={size} $styleProps={styleProps}>
              {effectiveLabels.bottom}
            </InputLabel>
            <NumberInputWithUnit
              variant={variant}
              size={size}
              error={error}
              styleProps={styleProps}
              value={currentValues.bottom}
              unitOptions={unitOptions}
              onChange={(combinedValue) => handleValueChange('bottom', combinedValue)}
              placeholder={placeholder}
              disabled={disabled}
              min={min}
              max={max}
              step={step}
              precision={precision}
              allowNegative={allowNegative}
              showControls={showControls}
            />
          </InputWrapper>

          {}
          <InputWrapper $position='left' $layout={layout}>
            <InputLabel $size={size} $styleProps={styleProps}>
              {effectiveLabels.left}
            </InputLabel>
            <NumberInputWithUnit
              variant={variant}
              size={size}
              error={error}
              styleProps={styleProps}
              value={currentValues.left}
              unitOptions={unitOptions}
              onChange={(combinedValue) => handleValueChange('left', combinedValue)}
              placeholder={placeholder}
              disabled={disabled}
              min={min}
              max={max}
              step={step}
              precision={precision}
              allowNegative={allowNegative}
              showControls={showControls}
            />
          </InputWrapper>

          {}
          <CenterButton $size={size} $layout={layout} $styleProps={styleProps}>
            <LinkButton
              type='button'
              $linked={currentLinked}
              $size={size}
              $disabled={disabled}
              $styleProps={styleProps}
              onClick={handleLinkToggle}
              disabled={disabled}
              title={currentLinked ? 'Unlink values' : 'Link values'}
              aria-label={currentLinked ? 'Unlink values' : 'Link values'}
            >
              <LinkIconComponent />
            </LinkButton>
          </CenterButton>
        </InputsGrid>
      </Content>
    </Container>
  );
};